import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { ApiClient, Heizungberechnung, HeizungHeizflaechenliste } from '../api';
import { Rohrnetzansicht } from './HeizungRohrnetz';
import {
  istSchranke, mitSchranke, Schrankenmarke, schrankentext,
} from './heizung/schranke';
import { Daemmnachweisansicht } from './HeizungDaemmung';
import type { Rahmen } from './Projektrahmen';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe,
  LoadingState, StatusBadge, formatiereZahl, type ExportZiel, type Spalte, type Zustand,
} from '../muster';
import { zeitpunkt } from './heizung/kaelte';

/**
 * Die Rohrnetzberechnung — Stufe 5 des Planungsmodells.
 *
 * ═══ Was die Seite zeigt und in welcher Reihenfolge ═══════════════════════
 *
 * Oben je Auslegungsgruppe eine Zeile mit der Förderhöhe. Das ist die Zahl,
 * nach der eine Pumpe beschafft wird, und sie steht deshalb vorn.
 *
 * Darunter die Wege, der maßgebende zuerst und dann nach Abgleichdruck
 * absteigend. Das ist die Reihenfolge, in der ein Monteur die
 * Voreinstellungen einstellt — wer die größte Drosselung zuerst setzt,
 * merkt am schnellsten, ob die Rechnung stimmt.
 *
 * ═══ Warum der Befund oben steht und nicht unten ══════════════════════════
 *
 * 578 von 791 Verbrauchern haben keinen Weg zu einer Quelle. Eine Förderhöhe
 * darüber wäre eine Zahl, die für ein Viertel der Anlage gilt und aussieht,
 * als gälte sie für alles.
 *
 * Deshalb steht der Befund VOR den Ergebnissen und nicht als Fußnote. Wer
 * ihn übersieht, hat ihn nicht gesucht.
 */
export function HeizungBerechnung() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [daten, setDaten] = useState<Heizungberechnung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [lauf, setLauf] = useState<string[] | null>(null);
  const [gruppe, setGruppe] = useState<string>('');

  // Die Heizflächen hängen am PROJEKT und nicht an der gewählten
  // Auslegungsgruppe - ein Heizkörper ohne Weg zu einer Quelle gehört zu
  // keiner Gruppe. Eigener Ladevorgang, eigener Fehlerzweig: Fehlt die
  // Prüfung, verschwindet der Abschnitt, und die Rohrnetztabelle bleibt.
  const [heizflaechen, setHeizflaechen] =
    useState<HeizungHeizflaechenliste | null>(null);

  // ═══════════════════════════════════════════════════════════════════════
  // GESAMTAUSWERTUNG UND VERGLEICHSRECHNUNG
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Seit Migration 0489 liefert der Endpunkt je gemischter Gruppe
  // ZUSAETZLICHE Zeilen - eine je Verbraucherklasse (Befund H-14). Sie sind
  // an einer gefüllten `verbraucherklasse` erkennbar.
  //
  // Die Trennung ist keine Darstellungsfrage: Ungefiltert stünde H_HZG
  // dreimal in der Übersicht, mit drei verschiedenen Förderhöhen und ohne
  // Hinweis darauf, welche die Anlage beschreibt.
  // `?? []` an BEIDEN Stellen: Eine Antwort ohne `netze` ist möglich, und
  // `daten.netze.length` brach die Seite ab, statt den Leerzustand zu zeigen.
  const netze = daten?.netze ?? [];
  const gesamtzeilen = netze.filter((n) => n.verbraucherklasse == null);


  // Ein Ausgabeknopf darf nicht still scheitern: Seit TH-2 übernimmt das die
  // ExportLeiste (Fehler als ErrorState klein unter der Leiste, „fertig“ über
  // aria-live). Ein gescheiterter Download nimmt die Tabelle nicht weg.

  const laden = async () => {
    try {
      setDaten(await api.heizungBerechnung(projekt.id,
        gruppe === '' ? undefined : gruppe));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void laden(); }, [api, projekt.id, gruppe]);

  useEffect(() => {
    let abgebrochen = false;

    // TH-2: ohne Kappung - die Grenze liegt über jedem Projekt; meldet der
    // Server trotzdem `gekuerzt`, steht das unter der Tabelle.
    api.heizungHeizflaechen(projekt.id, { grenze: 5000 })
      .then((h) => { if (!abgebrochen) setHeizflaechen(h); })
      .catch(() => { if (!abgebrochen) setHeizflaechen(null); });

    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  const rechnen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const e = await api.heizungBerechnen(projekt.id);
      setLauf(e && typeof e === 'object' && 'hinweise' in e
        ? (e as { hinweise: string[] }).hinweise : null);
      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const kpa = (pa: number | null | undefined) =>
    formatiereZahl(pa === null || pa === undefined ? null : pa / 1000, 1);

  const gueteZustand = (guete: string | null): Zustand =>
    (guete === 'gruen' ? 'gut'
      : guete === 'gelb' ? 'warnung'
        : guete === 'rot' ? 'fehler' : 'neutral');

  const gueteText = (guete: string | null) =>
    (guete === 'gruen' ? 'im Rahmen'
      : guete === 'gelb' ? 'prüfen'
        : guete === 'rot' ? 'außerhalb' : 'ohne Bewertung');

  const netzspalten: Spalte<Heizungberechnung['netze'][number]>[] = [
    {
      schluessel: 'gruppe', titel: 'Gruppe', immerSichtbar: true,
      wert: (n) => n.systemgruppe,
      inhalt: (n) => (
        <>
          <button
            className="m-knopf m-knopf-still"
            onClick={() => setGruppe(gruppe === n.systemgruppe ? '' : n.systemgruppe)}
          >
            {n.systemgruppe}
          </button>
          {(n.verbraucherklassen ?? 0) > 1 && (
            <StatusBadge
              zustand="warnung"
              klein
              text={`${n.verbraucherklassen} Verbraucherklassen gemischt`}
              titel={'Förderhöhe und Abgleich gelten für den ungünstigsten Weg über '
                + 'alle Klassen. Die Auswertung je Klasse steht unten.'}
            />
          )}
        </>
      ),
    },
    {
      schluessel: 'kreis', titel: 'Kreis', wert: (n) => n.kreis,
      inhalt: (n) => <span className="schwach">{n.kreis ?? '—'}</span>,
    },
    {
      schluessel: 'vorlauf', titel: 'Vorlauf / Rücklauf', einheit: '°C',
      ausrichtung: 'rechts', wert: (n) => n.vorlaufC,
      inhalt: (n) => <>{formatiereZahl(n.vorlaufC, 1)} / {formatiereZahl(n.ruecklaufC, 1)}</>,
    },
    {
      schluessel: 'wege', titel: 'Wege mit Ergebnis', ausrichtung: 'rechts',
      wert: (n) => n.wegeMitErgebnis,
      inhalt: (n) => <>{n.wegeMitErgebnis} von {n.wege}</>,
    },
    {
      schluessel: 'volumenstrom', titel: 'Volumenstrom', art: 'zahl',
      einheit: 'm³/h', nachkomma: 1, wert: (n) => n.volumenstromM3h,
    },
    {
      // ═══ F72 ═══════════════════════════════════════════════════════════
      //
      // Das `≥` gehört an die Zahl. Die Grundgesamtheit stand vorher zwei
      // Spalten links ("225 von 635") — richtig, vollständig und von
      // niemandem mit dieser Zelle verbunden. Wer die Spalte Förderhöhe in
      // eine Tabelle kopierte, kopierte eine Zahl ohne Vorbehalt.
      //
      // `wert` bleibt die reine Zahl: Danach wird sortiert, und eine
      // Schranke sortiert sich nach ihrem Wert.
      schluessel: 'foerderhoehe', titel: 'Förderhöhe', art: 'zahl', einheit: 'kPa',
      nachkomma: 1,
      wert: (n) => (n.foerderhoehePa == null ? null : n.foerderhoehePa / 1000),
      inhalt: (n) => (
        <span title={istSchranke(n.vollstaendigkeit)
          ? schrankentext(n.wege, n.wegeMitErgebnis) : undefined}>
          <strong>{mitSchranke(kpa(n.foerderhoehePa), n.vollstaendigkeit)}</strong>
          {' '}
          <Schrankenmarke vollstaendigkeit={n.vollstaendigkeit}
                          wege={n.wege} wegeMitErgebnis={n.wegeMitErgebnis} />
        </span>
      ),
    },
    {
      schluessel: 'mws', titel: 'Förderhöhe', art: 'zahl', einheit: 'm WS',
      nachkomma: 2, wert: (n) => n.foerderhoeheMWS,
      inhalt: (n) => (
        <>{mitSchranke(formatiereZahl(n.foerderhoeheMWS, 2), n.vollstaendigkeit)}</>
      ),
    },
    {
      schluessel: 'guete', titel: 'Güte', art: 'status', breite: '150px',
      wert: (n) => gueteText(n.guete),
      inhalt: (n) => (
        <StatusBadge zustand={gueteZustand(n.guete)} text={gueteText(n.guete)} klein />
      ),
    },
  ];

  const kopf = (
    <PageHeader
      titel="Rohrnetzberechnung Heizung"
      beschreibung={'Ströme von den Verbrauchern rückwärts, Rohrreibung nach '
        + 'Colebrook-White, Einzelwiderstände über ζ. Ein Weg ist Vorlauf plus '
        + 'Verbraucher plus Rücklauf — die drei Hälften addieren sich.'}
      meta={rechenstandRohrnetz(netze)}
      aktionen={(
        <>
          <button className="m-knopf m-knopf-haupt" disabled={laeuft}
                  onClick={() => void rechnen()}>
            {laeuft ? 'rechnet …' : 'Rohrnetz berechnen'}
          </button>
          <ExportLeiste ziele={exportziele(api, projekt.id, gruppe, netze.length > 0)} />
          <Link className="m-knopf m-knopf-still" to={`/projekte/${id}/heizung-kaelte`}>Heizung und Kälte</Link>
        </>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Rohrnetzberechnung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); void laden(); }}
        />
      </>
    );
  }

  if (!daten) {
    return (
      <>
        {kopf}
        <LoadingState text="Rohrnetzberechnung wird geladen …" zeilen={8} spalten={6} />
      </>
    );
  }

  return (
    <>
      {kopf}

      {!gruppe && (
        <p className="schwach">
          Funktions- und Strangschema gelten je Kreis — in der Tabelle eine
          Systemgruppe wählen; dann stehen sie in der Exportleiste.
        </p>
      )}

      {lauf && lauf.length > 0 && lauf.map((h) => (
        <p key={h} className={h.startsWith('ACHTUNG') ? 'hinweis fehler' : 'hinweis'} role="note">{h}</p>
      ))}

      {/*
        Die Gesamtauswertung ist das Ergebnis der Anlage; die Zeilen mit
        gefüllter Verbraucherklasse sind die Vergleichsrechnung aus H-14.
        Ungefiltert stünde dieselbe Gruppe drei- und viermal in der Übersicht.
      */}
      {netze.length === 0 && (
        <EmptyState
          titel="Für dieses Modell wurde noch keine Rohrnetzberechnung durchgeführt."
          erklaerung={'Der Lauf setzt voraus, dass die Teilstrecken gebildet sind und '
            + 'jede Auslegungsgruppe Stoffwerte trägt.'}
          aktion={(
            <button className="m-knopf m-knopf-haupt" disabled={laeuft}
                    onClick={() => void rechnen()}>
              {laeuft ? 'rechnet …' : 'Rohrnetz berechnen'}
            </button>
          )}
        />
      )}

      {gesamtzeilen.length > 0 && (
        <ContentSection
          titel="Förderhöhe je Auslegungsgruppe"
          beschreibung={'Je Gruppe und nicht je Kreis: „Kälte“ umfasst 420_CHW mit '
            + '6/12 °C und K_Segel mit 16/19 °C. Eine gemeinsame Förderhöhe für beide '
            + 'wäre die Förderhöhe von keinem.'}
          nackt
        >
          <DataTable
            name="Auslegungsgruppen"
            spalten={netzspalten}
            zeilen={gesamtzeilen}
            schluessel={(n) => String(n.id)}
            ausfuhr
            ausfuhrDateiname="foerderhoehe-je-gruppe"
            aktiveZeile={gesamtzeilen.find((n) => n.systemgruppe === gruppe)?.id}
            leer={(
              <EmptyState
                titel="Keine Auslegungsgruppe mit Ergebnis."
                erklaerung="Ohne Weg von einem Verbraucher zu einer Quelle entsteht keine Förderhöhe."
              />
            )}
          />
        </ContentSection>
      )}

      {/* ═══ Die Gliederung: Gruppe → Klasse → Weg → Teilstrecke ═══════
          Schritt 2c des Normprogramms. Die flache Wegtabelle stand hier bis
          zum 16.09.2026; sie zeigte 400 von 635 Wegen und den Rechenweg erst
          nach dem Aufklappen einer Zeile - ohne Teilstrecken und ohne die
          Herkunft der Eingangsgrößen. */}
      <Rohrnetzansicht api={api} projektId={projekt.id} daten={daten} />

      <Heizflaechenabgleich liste={heizflaechen} />

      {/* Der Daemmnachweis (HZ4). Er steht auf dieser Seite, weil die
          Daemmung an denselben Leitungen haengt wie der Druckverlust -
          und weil die Mindestdicke der Anlage 8 gesetzliche Pflicht ist
          und nicht in eine Nebenansicht gehoert. */}
      <Daemmnachweisansicht api={api} projektId={projekt.id} />
    </>
  );
}

/**
 * Die Heizflächenauslegung nach DIN EN 442-2 — Befunde H-01 und H-13 der
 * Normprüfung vom 16.09.2026.
 *
 * ═══ Warum der Abschnitt auf DIESER Seite steht ══════════════════════════
 *
 * Weil hier der Massenstrom zum Druckverlust wird. Die Frage „passt die
 * Leistung zum Massenstrom“ entscheidet, ob die Förderhöhe darüber etwas
 * wert ist — an derselben Stelle gehört die Antwort hin.
 *
 * ═══ Warum der Abschnitt ohne Prüflauf verschwindet ══════════════════════
 *
 * Ein leerer Kasten mit „0 Heizflächen“ läse sich wie „geprüft und in
 * Ordnung“, und geprüft wäre dann gar nichts. Dieselbe Regel wie beim
 * Luftmengenabgleich der Lüftung.
 *
 * ═══ Warum zwei Verteilungen nebeneinander stehen ════════════════════════
 *
 * `widerspruch` schlägt die Deckungsstufen — wenn sich Leistung und
 * Massenstrom desselben Bauteils um den Faktor 7,5 widersprechen, ist jede
 * Deckungsaussage eine Aussage über die falsche Zahl. Im Bestand trifft das
 * 264 von 312 Zeilen. Ohne die zweite Verteilung wäre nicht zu sehen, wie
 * die Deckung ohne den Widerspruch ausfiele.
 */
function Heizflaechenabgleich(
  { liste }: { liste: HeizungHeizflaechenliste | null },
) {
  if (!liste || liste.summe.gesamt === 0) return null;

  const kw = (w: number | null | undefined) =>
    formatiereZahl(w === null || w === undefined ? null : w / 1000, 1);

  const zahlJe = (stufe: string) =>
    liste.jeBewertung.find((b) => b.stufe === stufe)?.anzahl ?? 0;

  const unbestaetigt = liste.ansaetze.filter((a) => !a.istBestaetigt).length;

  const bewertungZustand = (stufe: string): Zustand =>
    (stufe === 'widerspruch' || stufe === 'unter' ? 'fehler'
      : ['ueber', 'ohne_kennwert', 'ohne_heizlast'].includes(stufe) ? 'warnung'
        : 'neutral');

  const zeilenspalten: Spalte<HeizungHeizflaechenliste['zeilen'][number]>[] = [
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', breite: '170px',
      wert: (z) => z.bewertung,
      inhalt: (z) => (
        <StatusBadge zustand={bewertungZustand(z.bewertung)} text={z.bewertung} klein />
      ),
    },
    {
      schluessel: 'raum', titel: 'Raum', immerSichtbar: true, wert: (z) => z.raumnummer,
      inhalt: (z) => (
        <>
          {z.raumnummer ?? '—'}
          {z.geraeteImRaum !== null && z.geraeteImRaum > 1 && (
            <span className="schwach"> ×{z.geraeteImRaum}</span>
          )}
        </>
      ),
    },
    {
      schluessel: 'typ', titel: 'Typ', wert: (z) => z.typbezeichnung,
      inhalt: (z) => <span className="schwach">{z.typbezeichnung ?? '—'}</span>,
    },
    {
      schluessel: 'baugroesse', titel: 'Baugröße',
      wert: (z) => z.baureiheKennung ?? z.baureihe,
      inhalt: (z) => (
        <span className="schwach">{z.baureiheKennung ?? (z.baureihe ?? '—')}</span>
      ),
    },
    {
      schluessel: 'normleistung', titel: 'Φ Norm', art: 'zahl', einheit: 'W',
      wert: (z) => z.normleistungW,
      titelHinweis: 'Normleistung nach DIN EN 442-2 bei 75/65 °C gegen 20 °C',
    },
    {
      schluessel: 'exponent', titel: 'n', art: 'zahl', nachkomma: 3,
      wert: (z) => z.exponent,
      titelHinweis: 'Heizkörperexponent; ≈ kennzeichnet einen Anhaltswert je Bauart',
      inhalt: (z) => (
        <span title={z.exponentHerkunft ?? undefined}>
          {formatiereZahl(z.exponent, 3)}
          {z.exponentHerkunft === 'ansatz' && <span className="schwach"> ≈ (Anhaltswert je Bauart)</span>}
        </span>
      ),
    },
    {
      schluessel: 'dtart', titel: 'Δt-Art', wert: (z) => z.uebertemperaturArt,
      inhalt: (z) => (
        <span className="schwach">
          {z.uebertemperaturArt === 'arithmetisch' ? 'arithm.'
            : z.uebertemperaturArt === 'logarithmisch' ? 'log.' : '—'}
        </span>
      ),
    },
    {
      schluessel: 'dtb', titel: 'Δt Betrieb', art: 'zahl', einheit: 'K',
      nachkomma: 2, wert: (z) => z.betriebUebertemperaturK,
    },
    {
      schluessel: 'betrieb', titel: 'Φ Betrieb', art: 'zahl', einheit: 'W',
      wert: (z) => z.betriebsleistungW,
      inhalt: (z) => <strong>{formatiereZahl(z.betriebsleistungW, 0)}</strong>,
    },
    {
      schluessel: 'ausmassenstrom', titel: 'aus ṁ', art: 'zahl', einheit: 'W',
      wert: (z) => z.leistungAusMassenstromW,
    },
    {
      schluessel: 'modell', titel: 'Modellwert', art: 'zahl', einheit: 'W',
      wert: (z) => z.modellleistungW,
    },
    {
      schluessel: 'heizlast', titel: 'Heizlast Raum', art: 'zahl', einheit: 'W',
      wert: (z) => z.heizlastRaumW,
    },
    {
      schluessel: 'deckung', titel: 'Deckung', art: 'zahl', einheit: '%',
      wert: (z) => z.deckungProzent,
    },
    {
      schluessel: 'verhaeltnis', titel: 'Q/ṁ', art: 'zahl', nachkomma: 2,
      wert: (z) => z.verhaeltnis,
      titelHinweis: 'Verhältnis von Leistung zu der Leistung aus dem Massenstrom',
      inhalt: (z) => (z.bewertung === 'widerspruch'
        ? <StatusBadge zustand="fehler" text={formatiereZahl(z.verhaeltnis, 2)} klein />
        : <>{formatiereZahl(z.verhaeltnis, 2)}</>),
    },
  ];

  return (
    <ContentSection
      titel={`Heizflächen: ${liste.summe.gesamt} in ${liste.summe.raeume} Räumen nach DIN EN 442-2 umgerechnet`}
      aufklappbar
      standardOffen={zahlJe('widerspruch') > 0}
      meta={(
        <>
          {zahlJe('widerspruch') > 0 && (
            <StatusBadge zustand="fehler" text={`${zahlJe('widerspruch')} widersprüchlich`} klein />
          )}
          {zahlJe('ohne_kennwert') > 0 && (
            <StatusBadge zustand="warnung" text={`${zahlJe('ohne_kennwert')} ohne Kennwert`} klein />
          )}
        </>
      )}
    >
      <p className="schwach">
        Φ<sub>Betrieb</sub> = Φ<sub>Norm</sub> ·
        {' '}(Δt<sub>log,Betrieb</sub> / Δt<sub>log,Norm</sub>)<sup>n</sup>
        {' '}nach DIN EN 442-2, mit Δt<sub>log</sub> =
        {' '}(t<sub>V</sub> − t<sub>R</sub>) / ln((t<sub>V</sub> −
        {' '}t<sub>i</sub>)/(t<sub>R</sub> − t<sub>i</sub>)). Der Normpunkt
        liegt bei 75/65 °C gegen 20 °C. Wie Δt gebildet wird, entscheidet die
        BAUREIHE und nicht die Rechnung: arithmetisch gegen 50 K, wie Kermi
        seinen Exponenten ermittelt hat, oder logarithmisch gegen 49,83 K, wo
        keine Baureihe zugeordnet ist. Die beiden Wege liegen 4,5 %
        auseinander; die Spalte „Δt-Art" nennt den angesetzten. Der Exponent n
        stammt, wo vorhanden, vom Bauteil selbst (Kennfeld des Herstellers),
        sonst aus dem Katalog der Baugröße, sonst aus dem Ansatz je Bauart —
        und der ist ein Anhaltswert.
        {unbestaetigt > 0 && (
          <> <strong>{unbestaetigt} von {liste.ansaetze.length} Ansätzen sind
          unbestätigt.</strong></>
        )}
      </p>

      {liste.widerspruch && (
        <p className="hinweis fehler" role="note">{liste.widerspruch.text}</p>
      )}

      {/* Die Karte, nach der der Fachplaner zuerst sieht: Wird das Gebäude
          warm? Alles darunter ist der Weg zu dieser Zahl. */}
      <KpiReihe>
        <KpiCard
          titel="Deckungsgrad Gebäude"
          wert={liste.summe.deckungGebaeudeProzent}
          einheit="%" nachkomma={1}
          vergleich={`${kw(liste.summe.betriebsleistungW)} kW installiert gegen `
            + `${kw(liste.summe.heizlastRaeumeW)} kW Heizlast`}
          fussnote="DIN EN 442-2"
        />
        <KpiCard
          titel="Räume unterdeckt"
          wert={liste.jeDeckung.find((d) => d.stufe === 'unterdeckt')?.anzahl ?? 0}
          vergleich={`von ${liste.summe.raeume} Räumen mit Heizfläche`}
          zustand={(liste.jeDeckung.find((d) => d.stufe === 'unterdeckt')?.anzahl ?? 0) > 0
            ? 'warnung' : 'gut'}
          zustandText={(liste.jeDeckung.find((d) => d.stufe === 'unterdeckt')?.anzahl ?? 0) > 0
            ? 'Heizfläche reicht nicht' : 'keiner'}
        />
        <KpiCard
          titel="Räume überdeckt"
          wert={liste.jeDeckung.find((d) => d.stufe === 'ueberdeckt')?.anzahl ?? 0}
        />
        <KpiCard
          titel="ohne Baureihe" wert={liste.summe.ohneBaureihe}
          vergleich={`${liste.summe.mitKennwert} mit Baugröße aus dem Katalog`}
          zustand={liste.summe.ohneBaureihe > 0 ? 'warnung' : 'gut'}
          zustandText={liste.summe.ohneBaureihe > 0
            ? 'Exponent aus dem Ansatz' : 'vollständig'}
        />
      </KpiReihe>

      {liste.jeGeschoss.length > 0 && (
        <p>
          <span className="schwach">Deckung je Geschoss: </span>
          {liste.jeGeschoss.map((g) => (
            <StatusBadge key={g.geschoss} klein zustand="neutral"
              text={`${g.geschoss}: ${formatiereZahl(g.deckungProzent, 0)} % (${kw(g.betriebsleistungW)} gegen `
                + `${kw(g.heizlastW)} kW, ${g.raeume} Räume)`} />
          ))}
        </p>
      )}

      <p className="schwach">
        Vier Zahlen für dieselbe Größe:
        {' '}Normleistung 75/65/20 <strong>{kw(liste.summe.normleistungW)} kW</strong>,
        {' '}Betriebsleistung <strong>{kw(liste.summe.betriebsleistungW)} kW</strong>,
        {' '}aus dem Massenstrom{' '}
        <strong>{kw(liste.summe.leistungAusMassenstromW)} kW</strong>,
        {' '}Modellwert am Bauteil{' '}
        <strong>{kw(liste.summe.modellleistungW)} kW</strong>
        {' '}— gegen eine Norm-Heizlast dieser Räume von{' '}
        <strong>{kw(liste.summe.heizlastRaeumeW)} kW</strong>.
      </p>

      <p>
        <span className="schwach">Bewertung: </span>
        {liste.jeBewertung.map((b) => (
          <StatusBadge
            key={b.stufe}
            zustand={bewertungZustand(b.stufe)}
            text={`${b.stufe}: ${b.anzahl}`}
            klein
          />
        ))}
      </p>

      <p>
        <span className="schwach">Deckung je Raum, ohne den Vorrang des Widerspruchs: </span>
        {liste.jeDeckung.map((b) => (
          <StatusBadge
            key={b.stufe}
            zustand={bewertungZustand(b.stufe)}
            text={`${b.stufe}: ${b.anzahl}`}
            klein
          />
        ))}
      </p>

      <DataTable
        name="Heizflächen"
        spalten={zeilenspalten}
        zeilen={liste.zeilen}
        schluessel={(z) => String(z.id)}
        trefferText={liste.gekuerzt
          ? `${liste.zeilen.length} gezeigt von ${liste.summe.gesamt} Heizflächen`
          : undefined}
        ausfuhr
        ausfuhrDateiname="heizflaechen"
        dicht
        leer={(
          <EmptyState
            titel="Keine Heizfläche umgerechnet."
            erklaerung="Ohne Normleistung und Exponent lässt sich keine Betriebsleistung bilden."
          />
        )}
      />

      {liste.gekuerzt && (
        <p className="schwach">
          Die Liste ist gekürzt; die Zahlen oben zählen alle
          {' '}{liste.summe.gesamt} Heizflächen.
        </p>
      )}
    </ContentSection>
  );
}

/**
 * Rechenstand nach TX-STANDARD S1.2: „Rohrnetzlauf gerechnet ‹Zeit›“ – der
 * jüngste Rechenzeitpunkt der Gesamtzeilen; ohne Lauf „noch nicht gerechnet“.
 */
export function rechenstandRohrnetz(netze: Heizungberechnung['netze']): string {
  const zeiten = netze.filter((n) => n.verbraucherklasse == null && n.berechnetAm)
    .map((n) => n.berechnetAm).sort();
  return zeiten.length === 0 ? 'noch nicht gerechnet'
    : `Rohrnetzlauf · gerechnet ${zeitpunkt(zeiten[zeiten.length - 1])}`;
}

/**
 * Die Exportleiste der Rohrnetzseite (S1.9): PDF Druckverlustnachweis (HZG-DRV,
 * gesamt und je gewählter Gruppe), die drei Schemata und die Excel-Mappe. Die
 * Schemata entstehen weiter je Abruf neu (Zeichner mit Einfang, TX §9.3) und
 * tragen deshalb den Hinweis; ohne Lauf sind die Knöpfe mit Grund gesperrt.
 */
export function exportziele(api: ApiClient, projektId: string, gruppe: string, gerechnet: boolean): ExportZiel[] {
  const ohneLauf = gerechnet ? undefined : { grund: 'Noch keine Rohrnetzberechnung – erst rechnen.' };
  const ziele: ExportZiel[] = [
    { art: 'pdf', beschriftung: 'PDF Druckverlust', laden: () => api.heizungBerichtPdf(projektId), gesperrt: ohneLauf },
  ];
  if (gruppe) {
    ziele.push({ art: 'pdf', beschriftung: `PDF Druckverlust ${gruppe}`,
      laden: () => api.heizungBerichtPdf(projektId, gruppe), gesperrt: ohneLauf });
  }
  ziele.push({ art: 'pdf', beschriftung: 'PDF Heizflächen', laden: () => api.heizungHeizflaechenPdf(projektId),
    gesperrt: ohneLauf });
  ziele.push({ art: 'pdf', beschriftung: 'Anlagenschema', neueFassungJeAbruf: true,
    laden: () => api.heizungAnlagenschemaPdf(projektId), gesperrt: ohneLauf });
  if (gruppe) {
    ziele.push(
      { art: 'pdf', beschriftung: `Funktionsschema ${gruppe}`, neueFassungJeAbruf: true,
        laden: () => api.heizungFunktionsschemaPdf(projektId, gruppe), gesperrt: ohneLauf },
      { art: 'pdf', beschriftung: `Strangschema ${gruppe}`, neueFassungJeAbruf: true,
        laden: () => api.heizungStrangschemaPdf(projektId, gruppe), gesperrt: ohneLauf },
    );
  }
  ziele.push({ art: 'excel', laden: () => api.heizungRohrnetzExcel(projektId), gesperrt: ohneLauf });
  return ziele;
}
